import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useEffect, useRef } from 'react';

import { Box, InputAdornment, TextField, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import * as doc from '../infoDoc';
import { useStyles } from '../styles';
import { Cluster, ClusterConfiguration } from '../types';
import { clusterInputErrors, namespaceInputErrors } from '../validation';

interface ClusterPickerProps {
  onChange: (value: ClusterConfiguration | null) => void;
  clusters: Cluster[];
  value: ClusterConfiguration;
  errors: string[];
  enableAppSetSupport?: boolean;
}

const getNamespacePrefix = (allowedNamespaces: string[]): string => {
  const first = allowedNamespaces[0];
  if (!first) return '';
  return first.endsWith('*') ? first.slice(0, -1) : '';
};

export const ClusterPicker = (props: ClusterPickerProps) => {
  const {
    onChange,
    value,
    clusters,
    errors,
    enableAppSetSupport = false,
  } = props;
  const classes = useStyles();
  const clusterRef = useRef<HTMLDivElement>(null);
  const namespaceRef = useRef<HTMLDivElement>(null);

  const selectedCluster =
    clusters.find(cluster => cluster.name === value?.name) || null;

  const clusterDisabled = clusters.length === 0;
  const namespaceDisabled = value.allowedNamespaces.length === 0;

  const namespacePrefix =
    !enableAppSetSupport && value.kaapTenant
      ? getNamespacePrefix(value.allowedNamespaces)
      : '';
  const namespaceSuffix =
    namespacePrefix && value.namespace.startsWith(namespacePrefix)
      ? value.namespace.slice(namespacePrefix.length)
      : value.namespace;

  const clusterError = errors.length > 0 ? clusterInputErrors(value) : '';
  const namespaceError =
    errors.length > 0
      ? namespaceInputErrors(
          value.namespace,
          value.allowedNamespaces,
          enableAppSetSupport,
        )
      : '';

  useEffect(() => {
    if (clusterError) {
      clusterRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    if (namespaceError) {
      namespaceRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [clusterError, namespaceError]);

  const handleClusterChange = (_: any, v: Cluster | null = null) => {
    const prefix =
      !enableAppSetSupport && v?.kaapTenant
        ? getNamespacePrefix(v?.namespaces || [])
        : '';
    onChange({
      name: v?.name || '',
      id: v?.id || '',
      server: v?.server || '',
      allowedNamespaces: v?.namespaces || [],
      namespace: prefix,
      ...(v?.kaapTenant && { kaapTenant: { name: v.kaapTenant.name } }),
    });
  };

  const handleNamespaceChange = (suffix: string) => {
    onChange({ ...value, namespace: namespacePrefix + suffix });
  };

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title="Cluster Details"
          description="Select the cluster and namespace."
          infoDoc={doc.CLUSTER_INFO}
        />
      </Box>
      <Box className={classes.inputContainer}>
        <Autocomplete
          size="small"
          value={selectedCluster}
          disabled={clusterDisabled}
          onChange={handleClusterChange}
          options={clusters}
          getOptionLabel={option => option.name}
          renderInput={params => {
            let clusterHelperText: React.ReactNode;
            if (clusterDisabled) {
              clusterHelperText =
                'Select a project first to see available clusters';
            } else if (clusterError) {
              clusterHelperText = (
                <ScaffolderFieldError message={clusterError} />
              );
            } else {
              clusterHelperText =
                'Select the cluster to deploy the application to.';
            }
            return (
              <TextField
                {...params}
                label="Cluster Name"
                ref={clusterRef}
                margin="none"
                variant="outlined"
                color="secondary"
                required
                InputProps={params.InputProps}
                error={Boolean(clusterError)}
                helperText={clusterHelperText}
              />
            );
          }}
          renderOption={option => (
            <Box>
              <Typography variant="body2" noWrap>
                {option.name}
              </Typography>
              {option.name && (
                <Typography variant="caption" noWrap color="textSecondary">
                  Cluster endpoint: {option.server}
                </Typography>
              )}
            </Box>
          )}
        />
      </Box>
      <Box className={classes.inputContainer}>
        <TextField
          label="Namespace"
          variant="outlined"
          margin="none"
          color="secondary"
          size="small"
          ref={namespaceRef}
          fullWidth
          required
          disabled={namespaceDisabled}
          value={namespaceSuffix}
          onChange={e => handleNamespaceChange(e.target.value)}
          error={Boolean(namespaceError)}
          inputProps={
            enableAppSetSupport
              ? undefined
              : { maxLength: 63 - namespacePrefix.length }
          }
          InputProps={
            namespacePrefix
              ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="subtitle1"
                        style={{ opacity: 0.6, whiteSpace: 'nowrap' }}
                      >
                        {namespacePrefix}
                      </Typography>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
          helperText={(() => {
            if (namespaceError) {
              return <ScaffolderFieldError message={namespaceError} />;
            }
            if (value.allowedNamespaces.length > 0) {
              return (
                <span>
                  Values must match:{' '}
                  <strong>{value.allowedNamespaces.join(', ')}</strong>
                </span>
              );
            }
            return 'The namespace to deploy the application into.';
          })()}
        />
      </Box>
    </Box>
  );
};
